import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Mission.css';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import Footer from '../../components/Footer'; 
import NextPageCTA from '../../components/NextPageCTA';
import logoImage from '../../assets/images/logo.png';

// Import images
import commitmentImg from '../../assets/images/commitment.jpg';
import excellenceImg from '../../assets/images/excellence.jpg';
import relationshipsImg from '../../assets/images/relationships.jpg';
import credentialsImg from '../../assets/images/credentials.jpg';
import developmentImg from '../../assets/images/development.jpg';

const Mission = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const missionRef = useRef(null);
  const textParagraphsRef = useRef([]);
  const isMissionInView = useInView(missionRef, { once: false, amount: 0.1 });
  
  // Animation variants
  const headingVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };
  
  const contentVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.6, delay: 0.2 }
    }
  };
  
  const listItemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: (i) => ({ 
      opacity: 1, 
      x: 0,
      transition: { 
        duration: 0.5, 
        delay: 0.3 + (i * 0.1)
      }
    })
  };

  // Add to refs helper function
  const addToRefs = (el, refs) => {
    if (el && !refs.current.includes(el)) {
      refs.current.push(el);
    }
  };

  // Core values with additional properties
  const coreValues = [
    {
      id: 1,
      title: "Commitment",
      content: "Committed to providing quality engineering services in the fields of Project Evaluation, Technical Audit, Monitoring and Due Diligence.",
      icon: "fa-solid fa-handshake",
      color: "#6a11cb",
      lightColor: "#f0e7ff",
      image: commitmentImg
    },
    {
      id: 2,
      title: "Excellence",
      content: "Provide our customers with complete range and highest quality of information and services.",
      icon: "fa-solid fa-chart-line",
      color: "#00c2cb",
      lightColor: "#e6fbfc",
      image: excellenceImg
    },
    {
      id: 3,
      title: "Relationships",
      content: "Building customer relationships by focusing on customer needs and striving to exceed their expectations through flexible customer driven solutions.",
      icon: "fa-solid fa-users",
      color: "#FF8008",
      lightColor: "#fff4e6",
      image: relationshipsImg
    },
    {
      id: 4,
      title: "Integrity",
      content: "Maintain high standards of professional conduct and to comply with all the rules and regulations relating to our work.",
      icon: "fa-solid fa-shield-halved",
      color: "#11998e",
      lightColor: "#e6f7f6",
      image: credentialsImg
    },
    {
      id: 5,
      title: "Development",
      content: "Develop cost effective solutions that meet specific needs of our clients consistent with long term development of the industry.",
      icon: "fa-solid fa-code",
      color: "#FF512F",
      lightColor: "#ffeae6",
      image: developmentImg
    }
  ];

  // Mission principles array
  const missionPrinciples = [
    {
      id: 1,
      text: "Committed to providing quality engineering services in the fields of project evaluation, technical audit, monitoring and due diligence.",
      icon: "fa-solid fa-check-double",
      iconClass: "mission-icon-1"
    },
    {
      id: 2,
      text: "Develop cost effective solutions that meet specific needs of our clients consistent with long term development of the industry.",
      icon: "fa-solid fa-code",
      iconClass: "mission-icon-2"
    },
    {
      id: 3,
      text: "Provide our customers with the complete range and highest quality of information and services.",
      icon: "fa-solid fa-chart-line",
      iconClass: "mission-icon-3"
    },
    {
      id: 4,
      text: "Building customer relationships by focusing on customer needs and striving to exceed their expectations through flexible customer driven solutions.",
      icon: "fa-solid fa-users",
      iconClass: "mission-icon-4"
    },
    {
      id: 5,
      text: "Maintain high standards of professional conduct and to comply with all the rules and regulations relating to our work.",
      icon: "fa-solid fa-shield-halved",
      iconClass: "mission-icon-5"
    }
  ];

  useEffect(() => {
    setIsVisible(true);
    
    // Observer for paragraph animation
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.classList.add('fade-in');
            entry.target.style.transitionTimingFunction = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
          }, 100);
        } else {
          // Remove fade-in class when scrolling up and out of view
          entry.target.classList.remove('fade-in');
        }
      });
    }, { threshold: 0.2, rootMargin: '0px 0px -10% 0px' });
    
    // Observe each paragraph
    textParagraphsRef.current.forEach(p => {
      observer.observe(p);
    });
    
    return () => {
      textParagraphsRef.current.forEach(p => {
        if (p) observer.unobserve(p);
      });
    };
  }, []);
  

  // Handle card interaction
  const handleCardClick = (id) => {
    setActiveCard(activeCard === id ? null : id);
  };

  return (
    <div>
    <div className="mission-modern">
      {/* Mission Logo */}
      <motion.img 
        src={logoImage} 
        alt="Vardaan Logo" 
        className="mission-page-logo"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      />

      {/* Mission Wrapper */}
      <div className="vision-mission-wrapper">
        <div className="container">
          <div className="vision-mission-content mission-only">
            {/* Mission Content Section */}
            <section className={`mission-content-section ${isVisible ? 'visible' : ''}`} ref={missionRef} style={{ marginBottom: '10px', minHeight: 'calc(100vh - 60vh - 130px)' }}>
              <motion.div 
                className="mission-overlay-container"
                initial="hidden"
                animate={isMissionInView ? "visible" : "hidden"}
                variants={{
                  hidden: { opacity: 0 },
                  visible: { 
                    opacity: 1,
                    transition: { 
                      staggerChildren: 0.1,
                      delayChildren: 0.2
                    }
                  }
                }}
              >
                <motion.div className="mission-header" variants={headingVariants}>
                  <h2>Our Mission</h2>
                  <div className="mission-decoration-modern">
                    <div className="circle"></div>
                    <div className="line"></div>
                    <div className="circle"></div>
                  </div>
                </motion.div>

                <motion.div className="mission-content-modern" variants={contentVariants}>
                  <div className="mission-quote text-content">
                    
                    <div className="mission-principles-list">
                      {missionPrinciples.map((principle, index) => (
                        <p 
                          key={principle.id}
                          ref={(el) => addToRefs(el, textParagraphsRef)}
                          className="mission-principle-item-overlay"
                        >
                          {principle.text}
                        </p>
                      ))}
                    </div>
                    <div className="mission-animated-underline"></div>
                  </div>
                  
                  <div className="mission-floating-shapes">
                    <div className="floating-shape shape1"></div>
                    <div className="floating-shape shape2"></div>
                    <div className="floating-shape shape3"></div>
                  </div>
                </motion.div>
              </motion.div>
            </section>
          </div>
        </div>
      </div>

      {/* Core Values - Interactive Cards with 3D Tilt Effect */}
      <section className={`core-values-section ${isVisible ? 'visible' : ''}`} style={{ paddingTop: '50px' }}>
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ marginTop: '20px', marginBottom: '40px' }}
          >
            <h2>Our Core Values</h2>
            <p>The principles that drive our work and define our company</p>
          </motion.div>

          <div className="core-values-grid">
            {coreValues.map((value) => (
              <motion.div
                key={value.id}
                className={`value-card ${activeCard === value.id ? 'active' : ''}`}
                style={{ 
                  borderColor: value.color
                }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ 
                  duration: 0.5, 
                  delay: value.id * 0.1,
                  ease: [0.175, 0.885, 0.32, 1.275]
                }}
                whileHover={{ 
                  y: -10,
                  boxShadow: `0 15px 30px rgba(0,0,0,0.2), 0 0 0 1px ${value.color}33`,
                  transition: { 
                    duration: 0.4,
                    ease: [0.175, 0.885, 0.32, 1.275]
                  }
                }}
                onClick={() => handleCardClick(value.id)}
              >
                <div className="value-card-bg" style={{ backgroundImage: `url(${value.image})` }}></div>
                <div className="value-content-wrapper">
                  <div className="value-icon" style={{ backgroundColor: value.color }}>
                    <i className={value.icon}></i>
                  </div>
                  <div className="value-content">
                    <h3>{value.title}</h3>
                    <p>{value.content}</p>
                  </div>
                  <div className="value-number">{value.id}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      </div>
      {/* Journey Navigation - Next Page CTA */}
      <NextPageCTA 
        title="Explore Our Services" 
        description="Discover our comprehensive range of engineering and consulting services designed to meet your project needs with precision and expertise."
        nextPage={{
          path: "/services",
          label: "View Our Services"
        }}
        headingText="Next step of your journey"
      />
      <Footer />
    </div>
  );
};

export default Mission; 